import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaUsers, FaUserPlus, FaUser, FaShieldAlt, FaExclamationCircle, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_ENDPOINTS } from '../apiConfig';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('user');
  const [errors, setErrors] = useState({});
  const [isDeleting, setIsDeleting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get(API_ENDPOINTS.GET_USERS);
        setUsers(response.data);
      } catch (error) {
        toast.error('Error fetching users');
      }
    };

    const userRole = localStorage.getItem('role');
    if (userRole !== 'superadmin') {
      toast.error('Access denied. Super Admins only.');
      navigate('/');
    } else {
      fetchUsers();
    }
  }, [navigate]);

  const handleAddUser = async (e) => {
    e.preventDefault();
    setErrors({});
    try {
      const response = await axios.post(API_ENDPOINTS.ADD_USER, {
        username,
        password,
        role,
      });
      toast.success(response.data.message || 'User added successfully');
      setUsers((prev) => [...prev, response.data.user]);
      setShowAddUserModal(false);
      setUsername('');
      setPassword('');
      setRole('user');
    } catch (error) {
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({
          general: error.response?.data?.message || 'Error adding user',
        });
      }
    }
  };

  const handleDeleteUser = async (userId) => {
    if (isDeleting) return;
    
    try {
      setIsDeleting(true);
      await axios.delete(API_ENDPOINTS.DELETE_USER(userId));
      setUsers((prevUsers) => prevUsers.filter((user) => user._id !== userId));
      toast.success('User deleted successfully');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.message || 'Error deleting user');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-8">
      <motion.div
        key="main-container"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-6xl mx-auto bg-white rounded-2xl shadow-lg p-4 sm:p-8"
      >
        <div className="flex flex-col sm:flex-row items-center justify-between mb-8 gap-4">
          <div className="flex items-center">
            <FaUsers className="w-8 h-8 text-[#080366] mr-3" />
            <h1 className="text-3xl font-bold text-[#080366]">
              User <span className="text-[#fbba00]">Management</span>
            </h1>
          </div>
          <motion.button
            key="add-user-button"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowAddUserModal(true)}
            className="w-full sm:w-auto bg-yellow-400 hover:bg-yellow-500 text-gray-800 px-6 py-3 rounded-lg font-semibold flex items-center justify-center transition-colors duration-200"
          >
            <FaUserPlus className="w-5 h-5 mr-2" />
            Add New User
          </motion.button>
        </div>

        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <motion.table 
              key="users-table"
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              className="w-full border-collapse"
            >
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="px-4 sm:px-6 py-4 text-left text-sm font-semibold text-gray-600">Username</th>
                  <th className="px-4 sm:px-6 py-4 text-left text-sm font-semibold text-gray-600">Role</th>
                  <th className="px-4 sm:px-6 py-4 text-left text-sm font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.map((user) => (
                  <motion.tr
                    key={user._id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="hover:bg-gray-50"
                  >
                    <td className="px-4 sm:px-6 py-4">
                      <div className="flex items-center">
                        <FaUser className="w-5 h-5 text-gray-400 mr-3" />
                        <span className="font-medium text-gray-700">{user.username}</span>
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-4">
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                          user.role === 'superadmin'
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-green-100 text-green-800'
                        }`}
                      >
                        <FaShieldAlt className="w-4 h-4 mr-2" />
                        {user.role}
                      </span>
                    </td>
                    <td className="px-4 sm:px-6 py-4">
                      {user.role !== 'superadmin' && (
                        <motion.button
                          key={`delete-${user._id}`}
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => handleDeleteUser(user._id)}
                          disabled={isDeleting}
                          className="text-red-500 hover:text-red-700 transition-colors duration-200"
                          title="Delete User"
                        >
                          <FaTrash className="w-5 h-5" />
                        </motion.button>
                      )}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </motion.table>
          </div>
        </div>

        {showAddUserModal && (
          <motion.div 
            key="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <motion.div 
              key="modal-content"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white rounded-2xl p-4 sm:p-8 shadow-xl w-full max-w-md"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <FaUserPlus className="w-6 h-6 text-blue-600 mr-3" />
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-800">Add New User</h3>
                </div>
                <button
                  onClick={() => {
                    setShowAddUserModal(false);
                    setErrors({});
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {errors.general && (
                <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-700">
                  <FaExclamationCircle className="w-5 h-5 mr-2 flex-shrink-0" />
                  <span className="text-sm">{errors.general}</span>
                </div>
              )}

              <form onSubmit={handleAddUser} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Username</label>
                  <input
                    type="text"
                    placeholder="Enter username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    className={`w-full px-4 py-3 rounded-lg border ${
                      errors.username ? 'border-red-300 ring-red-100' : 'border-gray-200'
                    } focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200`}
                  />
                  {errors.username && (
                    <div className="mt-2 flex items-center text-red-600">
                      <FaExclamationCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                      <span className="text-sm">{errors.username}</span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                  <input
                    type="password"
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className={`w-full px-4 py-3 rounded-lg border ${
                      errors.password ? 'border-red-300 ring-red-100' : 'border-gray-200'
                    } focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200`}
                  />
                  {errors.password && (
                    <div className="mt-2 flex items-center text-red-600">
                      <FaExclamationCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                      <span className="text-sm">{errors.password}</span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                  <select
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    className={`w-full px-4 py-3 rounded-lg border ${
                      errors.role ? 'border-red-300 ring-red-100' : 'border-gray-200'
                    } focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200`}
                  >
                    <option value="user">User</option>
                    <option value="superadmin">Super Admin</option>
                  </select>
                  {errors.role && (
                    <div className="mt-2 flex items-center text-red-600">
                      <FaExclamationCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                      <span className="text-sm">{errors.role}</span>
                    </div>
                  )}
                </div>

                <div className="flex justify-end space-x-4 mt-8">
                  <motion.button
                    key="cancel-button"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="button"
                    onClick={() => {
                      setShowAddUserModal(false);
                      setErrors({});
                    }}
                    className="px-6 py-3 rounded-lg text-gray-700 hover:bg-gray-100 
                             font-medium transition-colors duration-200"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    key="submit-button"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    className="px-6 py-3 rounded-lg bg-yellow-400 hover:bg-yellow-500 
                             text-gray-800 font-medium transition-colors duration-200"
                  >
                    Add User
                  </motion.button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default Users;